/**
 * HubSpot gated/demo forms are themed for dark cards (white labels, link-style
 * submit). On ResourceDetailV2 / contact light cards those colors vanish.
 * HubSpot re-injects theme rules after onFormReady, so we re-apply on every
 * subtree mutation and a short retry window (phone widget loads async).
 */

const LABEL_COLOR = '#374151';
const REQUIRED_COLOR = '#EF4444';
const BTN_BG = 'linear-gradient(135deg, #0098FF, #0057D4)';

const STYLE_ID = 'lb-hs-light-theme';

function ensureGlobalStyle() {
  if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    .lb-hs-form .hs-form-field > label,
    .lb-hs-form .hs-form-field > label span:not(.hs-form-required) {
      color: ${LABEL_COLOR} !important;
      -webkit-text-fill-color: ${LABEL_COLOR} !important;
    }
    .lb-hs-form .hs-form-required { color: ${REQUIRED_COLOR} !important; }
    .lb-hs-form .hs-button,
    .lb-hs-form input[type="submit"] {
      width: 100% !important;
      margin-top: 8px !important;
      padding: 13px 24px !important;
      border: none !important;
      border-radius: 12px !important;
      cursor: pointer !important;
      background: ${BTN_BG} !important;
      color: #fff !important;
      font-size: 15px !important;
      font-weight: 700 !important;
      line-height: 1.2 !important;
      box-shadow: 0 4px 16px rgba(0, 119, 255, 0.3) !important;
      text-decoration: none !important;
      appearance: none !important;
      -webkit-appearance: none !important;
    }
  `;
  document.head.appendChild(style);
}

export function applyHubSpotLightFormTheme(root) {
  if (!root || typeof document === 'undefined') return;
  ensureGlobalStyle();

  root.querySelectorAll('.hs-form-field > label').forEach((label) => {
    label.style.setProperty('color', LABEL_COLOR, 'important');
    label.style.setProperty('-webkit-text-fill-color', LABEL_COLOR, 'important');
    label.querySelectorAll('span:not(.hs-form-required)').forEach((span) => {
      span.style.setProperty('color', LABEL_COLOR, 'important');
      span.style.setProperty('-webkit-text-fill-color', LABEL_COLOR, 'important');
    });
  });

  root.querySelectorAll('.hs-form-required').forEach((el) => {
    el.style.setProperty('color', REQUIRED_COLOR, 'important');
  });

  root.querySelectorAll('.hs-button, input[type="submit"]').forEach((btn) => {
    btn.style.setProperty('color', '#ffffff', 'important');
    btn.style.setProperty('background', BTN_BG, 'important');
    btn.style.setProperty('border', 'none', 'important');
    btn.style.setProperty('width', '100%', 'important');
    btn.style.setProperty('text-decoration', 'none', 'important');
    btn.style.setProperty('appearance', 'none', 'important');
  });
}

/**
 * Keep labels + submit readable while HubSpot mutates the embed.
 * @returns {() => void} cleanup
 */
export function watchHubSpotLightFormTheme(root) {
  if (!root || typeof window === 'undefined') return () => {};

  applyHubSpotLightFormTheme(root);

  const observer = new MutationObserver(() => {
    applyHubSpotLightFormTheme(root);
  });
  observer.observe(root, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['style', 'class'],
  });

  const retryIds = [100, 400, 1000, 2500].map((ms) =>
    window.setTimeout(() => applyHubSpotLightFormTheme(root), ms),
  );

  return () => {
    observer.disconnect();
    retryIds.forEach((id) => window.clearTimeout(id));
  };
}
